import moment from "moment-timezone";
import { getBookingFilters } from "@components/booking/bookingFilters";
import { leadName } from "./model";

const METRIC_FIELDS = [
  ["Bookings", "bookings", false],
  ["Quotes sent", "quotesSent", false],
  ["Quote rate", "quoteRate", true],
  ["Closed bookings", "closedBookings", false],
  ["Close rate", "closeRate", true],
];

const nullable = (value) => value === undefined ? null : value;

function timestamp(value) {
  const parsed = value == null ? NaN : moment(value, moment.ISO_8601, true).valueOf();
  return Number.isFinite(parsed) ? parsed : null;
}

function taskBookingID(task) {
  return task.booking?.id || null;
}

function taskLead(task) {
  return task.lead || task.message?.lead || task.message?.toLead || null;
}

function newerTask(candidate, current) {
  if (!current) return true;
  const candidateTime = timestamp(candidate.createdAt);
  const currentTime = timestamp(current.createdAt);
  if (candidateTime == null || currentTime == null) throw new Error("Task dates are unavailable.");
  return candidateTime > currentTime || (candidateTime === currentTime && candidate.id.localeCompare(current.id) > 0);
}

function selectTasks(tasks) {
  const byBooking = new Map();
  const enquiries = new Map();
  tasks.forEach((task) => {
    if (!task.id || timestamp(task.createdAt) == null || !["pending", "done"].includes(task.status)) {
      throw new Error("Task details are incomplete.");
    }
    const bookingID = taskBookingID(task);
    if (bookingID) {
      if (newerTask(task, byBooking.get(bookingID))) byBooking.set(bookingID, task);
      return;
    }
    if (task.type !== "newEnquiryFollowup") return;
    const lead = taskLead(task);
    const key = task.message?.id ? `message:${task.message.id}` : lead?.id ? `lead:${lead.id}` : null;
    if (key && newerTask(task, enquiries.get(key))) enquiries.set(key, task);
  });
  const leadTasks = [...enquiries.values()].filter((task) => task.status === "pending").sort((a, b) => {
    const difference = timestamp(b.createdAt) - timestamp(a.createdAt);
    return difference || b.id.localeCompare(a.id);
  }).map((task) => {
    const lead = taskLead(task);
    return {
      id: task.id,
      leadId: lead?.id || null,
      name: leadName(lead),
      email: nullable(lead?.email),
      phone: nullable(lead?.phone),
      created: task.createdAt,
      task,
    };
  });
  return { byBooking, leadTasks };
}

function numeric(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function metricValue(value, rate) {
  const number = numeric(value);
  if (number == null) return null;
  return rate ? `${(number * 100).toLocaleString("en-AU", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
    : number.toLocaleString("en-AU");
}

function percentageChange(current, prior) {
  const value = numeric(current);
  const previous = numeric(prior);
  return value == null || previous == null || previous === 0 ? null : ((value - previous) / previous) * 100;
}

function monthToDateTone(statsTimestamp, now, current, prior) {
  const updatedAt = timestamp(statsTimestamp);
  const value = numeric(current);
  const previous = numeric(prior);
  if (updatedAt == null || value == null || previous == null) return "neutral";
  const asOf = moment(updatedAt);
  const today = moment(now);
  if (!today.isValid() || today.date() < 3 || asOf.date() < 3
    || !asOf.isSame(today, "month") || asOf.isAfter(today)) return "neutral";
  return value > previous ? "up" : value < previous ? "down" : "neutral";
}

function metricsView(stats, now) {
  return METRIC_FIELDS.map(([title, prefix, rate]) => ({
    title,
    last24: metricValue(stats[`${prefix}Last24Hours`], rate),
    last24Change: percentageChange(stats[`${prefix}Last24Hours`], stats[`${prefix}Last24HoursYoy`]),
    main: metricValue(stats[`${prefix}Last30Days`], rate),
    change: percentageChange(stats[`${prefix}Last30Days`], stats[`${prefix}Last30DaysYoy`]),
    seven: metricValue(stats[`${prefix}Last7Days`], rate),
    sevenChange: percentageChange(stats[`${prefix}Last7Days`], stats[`${prefix}Last7DaysYoy`]),
    mtd: metricValue(stats[`${prefix}MonthToDate`], rate),
    mtdChange: percentageChange(stats[`${prefix}MonthToDate`], stats[`${prefix}MonthToDateLastYear`]),
    mtdTone: monthToDateTone(stats.timestamp, now, stats[`${prefix}MonthToDate`], stats[`${prefix}MonthToDateLastYear`]),
    last: metricValue(stats[`${prefix}MonthLastYearTotal`], rate),
    priorSeven: metricValue(stats[`${prefix}Last7DaysYoy`], rate),
    priorMain: metricValue(stats[`${prefix}Last30DaysYoy`], rate),
    priorMtd: metricValue(stats[`${prefix}MonthToDateLastYear`], rate),
  }));
}

function requireList(value, label) {
  if (!Array.isArray(value)) throw new Error(`${label} are unavailable.`);
  return value;
}

export function buildDashboardPayload({ bookings, tasks, catalog, stats, now }) {
  requireList(bookings, "Bookings");
  requireList(tasks, "Tasks");
  if (!stats || typeof stats !== "object") throw new Error("Booking statistics are unavailable.");
  if (!Number.isFinite(now)) throw new Error("The current time is unavailable.");
  bookings.forEach((booking) => {
    if (!booking.id || !booking.reference) throw new Error("A booking reference is unavailable.");
  });

  const filters = getBookingFilters(bookings);
  const stagesByBooking = new Map();
  filters.forEach((filter) => filter.bookings.forEach((booking) => {
    const stages = stagesByBooking.get(booking.id);
    if (stages) stages.push(filter.type);
    else stagesByBooking.set(booking.id, [filter.type]);
  }));
  const selectedTasks = selectTasks(tasks);

  return {
    bookings,
    stagesByBooking,
    tasksByBooking: selectedTasks.byBooking,
    leadTasks: selectedTasks.leadTasks,
    catalog: requireList(catalog?.boats, "Boats"),
    metrics: metricsView(stats, now),
    statsUpdatedAt: nullable(stats.timestamp),
    stages: filters.map((filter) => ({ key: filter.type, label: filter.title, tooltip: filter.tooltip })),
  };
}
